import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getCardClass, isCardTransparent, getAnimation } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';

export default function ScheduleSection({ block = {}, primaryColor, secondaryColor, enableAnimations, cardStyle }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const days = content.days || ['Lunes', 'Martes', 'Miercoles', 'Jueves', 'Viernes', 'Sabado'];
    const slots = content.slots || [];
    const anim = getAnimation(config, visible);
    const tp = isCardTransparent(cardStyle, s.dark);
    const variant = config.variant || 'grid';

    if (slots.length === 0) return null;

    const cls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;
    const bgS = s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle };

    // Get unique times sorted
    const times = [...new Set(slots.map(sl => sl.time))].sort();

    // ── List variant ──
    if (variant === 'list') {
        const groupedByDay = {};
        days.forEach(d => { groupedByDay[d] = slots.filter(sl => sl.day === d); });

        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title || 'Horario'} subtitle={content.subtitle} config={config} s={s} />
                    <div className="space-y-6 max-w-3xl mx-auto">
                        {days.map((day, di) => {
                            const daySlots = groupedByDay[day];
                            if (!daySlots || daySlots.length === 0) return null;
                            return (
                                <div key={di} className={`transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                                    style={{ transitionDelay: visible ? `${di * 100}ms` : '0ms' }}>
                                    <h3 className="font-bold text-sm uppercase tracking-wider mb-3" style={{ color: s.accent }}>{day}</h3>
                                    <div className="space-y-2">
                                        {daySlots.map((sl, si) => (
                                            <div key={si} className="flex items-center gap-4 p-3 rounded-lg" style={{ backgroundColor: s.dark ? 'rgba(255,255,255,0.05)' : '#f9fafb' }}>
                                                <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: sl.color || s.accent }} />
                                                <span className="text-xs font-mono shrink-0" style={{ color: s.textSub }}>{sl.time}</span>
                                                <span className="font-semibold text-sm flex-1" style={{ color: s.text }}>{sl.title}</span>
                                                {sl.instructor && <span className="text-xs" style={{ color: s.textSub }}>{sl.instructor}</span>}
                                                {sl.duration && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: s.dark ? 'rgba(255,255,255,0.1)' : '#e5e7eb', color: s.textSub }}>{sl.duration}</span>}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Grid (default) ──
    return (
        <section className={cls} style={bgS}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                <SectionHeader tag={content.tag} title={content.title || 'Horario'} subtitle={content.subtitle} config={config} s={s} />
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[640px] border-collapse">
                        <thead>
                            <tr>
                                <th className="p-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: s.textSub }}>{content.time_label || 'Hora'}</th>
                                {days.map((day, i) => (
                                    <th key={i} className="p-3 text-center text-xs font-semibold uppercase tracking-wider" style={{ color: s.text }}>{day}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {times.map((time, ti) => (
                                <tr key={ti} className="border-t" style={{ borderColor: s.dark ? 'rgba(255,255,255,0.05)' : '#f3f4f6' }}>
                                    <td className="p-3 text-xs font-mono" style={{ color: s.textSub }}>{time}</td>
                                    {days.map((day, di) => {
                                        const slot = slots.find(sl => sl.time === time && sl.day === day);
                                        return (
                                            <td key={di} className="p-2 text-center">
                                                {slot ? (
                                                    <div className={`rounded-lg px-3 py-2 text-white text-xs font-medium transition-all duration-300 ${visible ? 'opacity-100 scale-100' : 'opacity-0 scale-90'}`}
                                                        style={{ backgroundColor: slot.color || s.accent, transitionDelay: visible ? `${(ti * days.length + di) * 50}ms` : '0ms' }}>
                                                        <div className="font-bold">{slot.title}</div>
                                                        {slot.instructor && <div className="opacity-80 mt-0.5">{slot.instructor}</div>}
                                                        {slot.duration && <div className="opacity-60 text-[10px] mt-0.5">{slot.duration}</div>}
                                                    </div>
                                                ) : (
                                                    <div className="h-10" />
                                                )}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
